import { useSelector } from 'react-redux';

import { selectUserProjectId } from 'core/userContext';
import { getDeployedRegion } from 'utils/env';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

/**
 * Generate a local storage key for a project in the following format: `{region}.project.{projectId}.{namespace}`
 *
 * @param projectId - The ID of the project
 * @param namespace - The prefix for the local storage key to avoid conflicts
 * @returns The key
 */
export const getProjectLocalStorageKey = (projectId: string | number, namespace: string) =>
  `${getDeployedRegion()}.project.${projectId}.${namespace}`;

/**
 * Project namespaced local storage hook
 *
 * @param namespace - The prefix for the local storage key to avoid conflicts
 * @param defaultValue - The default value to use if the key is not found in local storage
 */
export const useProjectLocalStorage = <Namespace extends string, Value = any>(
  namespace: Namespace,
  defaultValue?: Value,
) => {
  const projectId = useSelector(selectUserProjectId);
  const [value, setValue] = useLocalStorage(
    getProjectLocalStorageKey(projectId, namespace),
    defaultValue,
  );

  return [value, setValue] as const;
};
